import { I18N } from 'aurelia-i18n';
import { mock, instance, when, anything, verify } from 'ts-mockito';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Confirmation } from './confirmation';
import { PayrazrService } from '../../services/payrazr';
import { TemplateData } from '../../models/template-data';
import { DialogService } from 'aurelia-dialog';

describe('NewPortalWeb', () => {
	describe('components', () => {
		describe('confirmation', () => {
			let component: Confirmation;
			const mockI18N: I18N = mock(I18N);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockElement: Element = mock(Element);
			const mockEventAggregator: EventAggregator = mock(EventAggregator);
			const mockDialogService: DialogService = mock(DialogService);
			
			const i18next = {
				t: (a, b) => a
			};

			beforeEach(() => {

				when(mockI18N.i18next).thenReturn(i18next);

				component = new Confirmation(
					instance(mockElement),
					instance(mockPayrazrService),
					instance(mockI18N),
					instance(mockEventAggregator),
					instance(mockDialogService)
				);
			});

			it('should initialize Confirmation component', () => {
				expect(component).toBeTruthy();
			});

			describe('attached()', () => {
				it('should set default submitButtonText as Submit', () => {
					spyOn(i18next, 't').and.returnValue('Submit');
					component.attached();
					expect(component.submitButtonText).toBe('Submit');
				});
				it('should set default acceptButtonText as I Accept', () => {
					spyOn(i18next, 't').and.returnValue('I Accept');
					component.attached();
					expect(component.acceptButtonText).toBe('I Accept');
				});
				it('should show signature', () => {
					component.forceShowSignature = true;
					component.attached();
					expect(component.showSignature).toBeTrue();
				});
				it('should call chooseTemplate() and apply template', () => {
					spyOn(component, 'chooseTemplate').and.callThrough();
					spyOn(component, 'getTemplate').and.stub();
					component.templateData = new TemplateData({}, null, []);
					component.isCustomTemplate = false;
					component.attached();
					expect(component.chooseTemplate).toHaveBeenCalled();
					expect(component.getTemplate).toHaveBeenCalled();
				});
				it('should show accept terms and conditions', () => {
					component.forceAcceptanceOfTerms = true;
					component.attached();
					expect(component.showAcceptanceOfTerms).toBeTrue();
				});
			});

			describe('chooseTemplate()', () => {

				beforeEach(() => {
					component.templateData = new TemplateData({}, null, []);
				});

				it('should call getCustomTemplate()', () => {
					spyOn(component, 'getCustomTemplate').and.stub();
					component.isCustomTemplate = true;
					component.chooseTemplate();
					expect(component.getCustomTemplate).toHaveBeenCalled();
				});
				it('should call getTemplate() as ACH', () => {
					spyOn(component, 'getTemplate').withArgs('check', component.templateData).and.stub();
					component.isAch = true;
					component.chooseTemplate();
					expect(component.getTemplate).toHaveBeenCalledWith('check', component.templateData);
				});
				it('should call getTemplate() as Card', () => {
					spyOn(component, 'getTemplate').withArgs('card', component.templateData).and.stub();
					component.chooseTemplate();
					expect(component.getTemplate).toHaveBeenCalledWith('card', component.templateData);
				});
			});

			describe('getCustomTemplate()', () => {

				it('should set content as HTML and hashedContent as hashedValue from request result', async () => {

					when(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).thenResolve({
						templateHtml: '',
						hashedValue: ''
					});

					await component.getCustomTemplate(anything(), anything());
					verify(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).called();
					expect(component.content).not.toBe(null);
					expect(component.hashedContent).not.toBe(null);
				});

				it('should catch error also content and hashedContent should be undefined', async () => {

					when(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).thenReject(new Error());

					await component.getCustomTemplate(anything(), anything());
					verify(mockPayrazrService.applyTemplateAndReturnHash(anything(), anything())).called();
					expect(component.content).toBe(undefined);
					expect(component.hashedContent).toBe(undefined);
				});

			});

			describe('getTemplate()', () => {

				const tieredFeeTemplates = [
					{
						description: 'should Convenience Fee template have a table with id tblTieredFees',
						template: `<p> * By using this channel to make a payment on your account with {merchantDescriptor}, you are entering into an
						agreement with {merchantDescriptor} to expedite this payment at the cost of {fee}. There are other payment options
						available to you that do not include a fee. IF YOU DO NOT WANT TO PAY A FEE FOR MAKING AN ONLINE PAYMENT, PLEASE
						STOP NOW AND USE ONE OF THE OTHER AVAILABLE PAYMENT OPTIONS. For information on other payment options including
						options that do not include any additional fees, or questions about this fee, you may contact {merchantDescriptor}
						at {customerServicePhone}. </p> {tieredFeesTableStart} <br />
					<legend>Tiered Fee Amounts</legend>
					<table id="tblTieredFees" class="table table-striped table-condensed table-bordered">
						<tbody>
							<tr>
								<th>From</th>
								<th>Service Fee Value</th>
							</tr> <template> {tieredFeeStart} <tr>
									<td>{tierLmitAmount}</td>
									<td>{serviceFeeValue}</td>
								</tr> {tieredFeeEnd} </template>
						</tbody>
					</table> {tieredFeesTableEnd}`
					},
					{
						description: 'should Autopay Plan Confirmation template have a table with id tblTieredFees',
						template: `<p>Please review the following information and accept the below disclosure.</p>
						TL;TR
						{tieredFeesTableStart} <br />
						<legend>Tiered Fee Amounts</legend>
						<table id="tblTieredFees" class="table table-striped table-condensed table-bordered">
							<tbody>
								<tr>
									<th>From</th>
									<th>Service Fee Value</th>
								</tr> <template> {tieredFeeStart} <tr>
										<td>{tierLmitAmount}</td>
										<td>{serviceFeeValue}</td>
									</tr> {tieredFeeEnd} </template>
							</tbody>
						</table> {tieredFeesTableEnd}`
					},
					{
						description: 'should Autopay Plan Receipt template have a table with id tblTieredFees',
						template: `<p>Confirmation: Your Payment Plan is scheduled.</p>
						TL:TR {tieredFeesTableStart} <br />
						<legend>Tiered Fee Amounts</legend>
						<table id="tblTieredFees" class="table table-striped table-condensed table-bordered">
							<tbody>
								<tr>
									<th>From</th>
									<th>Service Fee Value</th>
								</tr> <template> {tieredFeeStart} <tr>
										<td>{tierLmitAmount}</td>
										<td>{serviceFeeValue}</td>
									</tr> {tieredFeeEnd} </template>
							</tbody>
						</table> {tieredFeesTableEnd}`
					}
				];

				it('should call payrazrService.getConfirmHtml to get template HTML content', async () => {

					when(mockPayrazrService.getConfirmHtml(anything(), anything())).thenResolve('<p>html content</p>');

					await component.getTemplate(anything(), anything());

					verify(mockPayrazrService.getConfirmHtml(anything(), anything())).called();
					expect(component.content).not.toBe(null);

				});

				tieredFeeTemplates.forEach((testCase) => {
					it(testCase.description, async () => {

						when(mockPayrazrService.getConfirmHtml(anything(), anything())).thenResolve(testCase.template);

						await component.getTemplate(anything(), anything());

						verify(mockPayrazrService.getConfirmHtml(anything(), anything())).called();
						expect(component.content).not.toBe(null);
						expect(component.content).toContain("id=\"tblTieredFees\"");
					});
				});

			});
		});
	});
});
